/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Area, Line } from '@ant-design/plots';
import { Input, DatePicker, message } from 'antd';
import moment from 'moment';

import {
  SectionCard,
  StatCard,
  ChartCard,
  FilterDropdown,
  FilterChips,
  DataTable,
} from '../../ui';
import {
  CHART_SEMANTIC,
  CHART_SERIES,
  CHART_AXIS,
} from '../../ui/chartPalette';
import { boardCompletion, boardGuidanceCourse } from '@/services/athenaBoard';
import styles from './board.less';
import { useUrlFilters } from '../../utils/useUrlFilters';
import {
  completionMemberColumns,
  courseDetailColumns,
} from './columns/serviceDeliveryColumns';

/**
 * 服务交付
 * ------------------------------------------------------------------
 * 两块：完课数据（h5 TenantStoreApi/memberData?dataType=2 口径）、
 * 指导课管理（h5 manage/guidance-dashboard.vue 口径）。
 *
 * 2026-09-04 按 h5 已有设计重做——此前我自己发明了柱子图和纯文字表格，
 * 而 h5 里现成就有：面积渐变折线、多系列色板、搜索框 + 语义色快速筛选
 * chip + 带头像的表格。
 *   · 销售与核销趋势 → 面积图 + 竖向渐变（主色 / 对比色）
 *   · 课程核销趋势   → 多系列折线，统一取 CHART_SERIES
 *   · 会员课时明细   → 搜索 + 快速筛选 chip + 头像表格
 *
 * 完课数据沿用 h5 的交互分叉：
 *   今天 / 昨天 → 完课会员列表（头像、昵称、开始时间、时长、课程名）
 *   其它        → dateClassList 折线，可切换完课次数 / 人数 / 人均次数
 *
 * 2026-09-17 展示层改造（方案 P2）—— **取数逻辑未动**：
 *   1. 两张明细表换 `DataTable`，列定义外置到 `columns/serviceDeliveryColumns.js`；
 *   2. 图表取色统一走 `ui/chartPalette`（原先本页自写白色/网格灰/轴标签灰）；
 *   3. 完课筛选（日期 / 会员类型）与趋势指标也写进 URL，与课时明细同约定
 *      （key 带域前缀 cd*，避免与其它页同名参数互相污染）；
 *   4. 两处请求补 `.catch`：原先只有 then，失败时静默、完课的 loading 还会一直转。
 */
const COURSE_FILTERS = [
  { value: 'all', label: '全部会员', tone: 'default' },
  { value: 'expire_7', label: '7日内到期', tone: 'danger' },
  { value: 'no_class_14', label: '超过7天未上课', tone: 'warning' },
  { value: 'less_5', label: '剩余课时少于5节', tone: 'primary' },
];

/** URL 里读回的值都是字符串：只有解析不出来时才回落默认值（`||` 会把合法的 0 误判掉） */
const toInt = (v, fallback) => {
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? fallback : n;
};

// 后端 dateRange=8 表示自定义时间区间
const DATE_RANGE_CUSTOM = 8;

const COURSE_PAGE_SIZE = 10;

const ServiceDelivery = (props) => {
  const { tenantId, coachUserId, enums = {} } = props;

  /* 筛选与 URL 同步（方案 §5.1）：
     · cd*    —— 完课数据（日期 / 会员类型 / 自定义区间 / 趋势指标）
     · course* —— 会员课时明细（筛选 / 页码） */
  const { filters, setFilter, setAll } = useUrlFilters({
    cdRange: 3,
    cdType: 0,
    cdStart: '',
    cdEnd: '',
    cdMetric: 'completedNum',
    courseFilter: 'all',
    coursePage: 1,
  });
  const dateRange = toInt(filters.cdRange, 3);
  const userType = toInt(filters.cdType, 0);
  /* 只有自定义区间才带日期，避免把上一次选的日期透给后端 */
  const startDate =
    dateRange === DATE_RANGE_CUSTOM ? filters.cdStart || null : null;
  const endDate =
    dateRange === DATE_RANGE_CUSTOM ? filters.cdEnd || null : null;
  const hasCompleteCustomRange =
    dateRange !== DATE_RANGE_CUSTOM || Boolean(startDate && endDate);
  const metric = filters.cdMetric || 'completedNum';

  const courseFilter = filters.courseFilter || 'all';
  const coursePage = toInt(filters.coursePage, 1);

  const [completion, setCompletion] = useState({});
  const [loading, setLoading] = useState(false);
  const [guidance, setGuidance] = useState({});
  const [keyword, setKeyword] = useState('');
  /* 搜索只在点「搜索」/回车时提交，避免每个字符一次请求 */
  const [search, setSearch] = useState('');

  /* 完课数据：依赖刻意列成基本类型（对象字面量每次渲染都是新引用，会让 effect 每次都跑） */
  useEffect(() => {
    let active = true;
    if (!tenantId || !coachUserId) return;
    // 自定义时间必须同时选中开始和结束时间，避免切换选项时提前请求
    if (!hasCompleteCustomRange) {
      setLoading(false);
      return;
    }
    setLoading(true);
    boardCompletion({
      tenantId,
      coachUserId,
      dateRange,
      userType,
      startDate,
      endDate,
    })
      .then((r) => {
        if (!active) return;
        setLoading(false);
        if (r && r.code === '00000') setCompletion(r.data || {});
        else message.error((r && r.msg) || '完课数据加载失败');
      })
      .catch(() => {
        if (!active) return;
        setLoading(false);
        message.error('完课数据加载失败，请稍后重试');
      });
    return () => {
      active = false;
    };
  }, [
    tenantId,
    dateRange,
    userType,
    startDate,
    endDate,
    hasCompleteCustomRange,
    coachUserId,
  ]);

  /* 课时明细：筛选与分页走服务端（源 listRights 支持 expireType/lastClassType/
     minRemainingClassNum/search，本地筛会在分页场景下漏掉非当前页数据） */
  useEffect(() => {
    if (!tenantId || !coachUserId) return;
    boardGuidanceCourse({
      tenantId,
      coachUserId,
      courseFilter,
      search,
      currentPage: coursePage,
      pageSize: COURSE_PAGE_SIZE,
    })
      .then((r) => {
        if (r && r.code === '00000') setGuidance(r.data || {});
        else message.error((r && r.msg) || '会员课时明细加载失败');
      })
      .catch(() => {
        message.error('会员课时明细加载失败，请稍后重试');
      });
  }, [tenantId, courseFilter, search, coursePage, coachUserId]);

  const summary = completion.summary || {};
  const list = completion.dateClassList || [];
  const metricLabel = (
    (enums.completionMetric || []).find((m) => m.value === metric) || {}
  ).label;

  /* ---------- 销售与核销趋势（面积 + 渐变） ---------- */
  const sellTrend = guidance.sellUsedTrend || {};
  const sellData = (sellTrend.labels || []).reduce(
    (acc, label, i) => [
      ...acc,
      { label, type: '销售课时', value: (sellTrend.sold || [])[i] || 0 },
      { label, type: '核销课时', value: (sellTrend.used || [])[i] || 0 },
    ],
    [],
  );

  /* ---------- 课程核销趋势（多系列） ---------- */
  const courseTrend = guidance.courseUsedTrend || {};
  const courseSeries = courseTrend.series || [];
  const courseData = courseSeries.reduce(
    (acc, s) => [
      ...acc,
      ...(s.data || []).map((value, j) => ({
        label: (courseTrend.labels || [])[j],
        type: s.name,
        value,
      })),
    ],
    [],
  );

  /* 服务端已筛过，本处不再本地过滤（courseRows 就是当前页） */
  const courseRows = guidance.courseDetailList || [];
  /* 今天/昨天口径下只出明细（源不给按日聚合），与 h5 同构 */
  const showMemberList = dateRange === 1 || dateRange === 2;

  return (
    <div className={styles.boardBody}>
      {/* ==================== 完课数据 ==================== */}
      <SectionCard
        title="完课数据"
        loading={loading}
        extra={
          <div className={styles.filterBar}>
            <FilterDropdown
              label="日期"
              value={dateRange}
              options={enums.dateRange || []}
              onChange={(v) =>
                /* 离开自定义区间时把日期一并清掉，避免 URL 里留一组用不到的日期 */
                v === DATE_RANGE_CUSTOM
                  ? setFilter('cdRange', v)
                  : setAll({ cdRange: v, cdStart: '', cdEnd: '' })
              }
            />
            {/* 画板要求完课数据可选「选择日期」；dateRange=8 靠 startDate/endDate 取区间 */}
            {dateRange === DATE_RANGE_CUSTOM && (
              <DatePicker.RangePicker
                allowClear={false}
                value={
                  startDate && endDate
                    ? [moment(startDate), moment(endDate)]
                    : undefined
                }
                onChange={(_v, strs) =>
                  setAll({
                    cdRange: DATE_RANGE_CUSTOM,
                    cdStart: strs?.[0] || '',
                    cdEnd: strs?.[1] || '',
                  })
                }
              />
            )}
            <FilterDropdown
              label="会员类型"
              value={userType}
              options={enums.userType || []}
              onChange={(v) => setFilter('cdType', v)}
            />
          </div>
        }
      >
        <div className={`${styles.kpiRow} ${styles.kpiRow3}`}>
          <StatCard title="完课次数" value={summary.completedNum} unit="次" />
          <StatCard
            title="完课人数"
            value={summary.completedUserNum}
            unit="人"
          />
          <StatCard
            title="人均完课次数"
            value={summary.completedPerUserNum}
            precision={2}
            unit="次"
          />
        </div>
      </SectionCard>

      {showMemberList ? (
        <SectionCard title="完课会员列表">
          <DataTable
            size="small"
            rowKey="userId"
            columns={completionMemberColumns}
            dataSource={completion.memberList || []}
            emptyText="该时段暂无完课会员"
            emptyHint="可切换日期范围再看"
            pagination={{ pageSize: 10, showSizeChanger: false }}
          />
        </SectionCard>
      ) : (
        <SectionCard
          title="完课趋势"
          subtitle={metricLabel}
          extra={
            <FilterDropdown
              label="指标"
              value={metric}
              options={enums.completionMetric || []}
              onChange={(v) => setFilter('cdMetric', v)}
            />
          }
        >
          <ChartCard height={320} empty={!list.length}>
            <Line
              height={320}
              smooth
              data={list}
              xField="date"
              yField={metric}
              color={CHART_SEMANTIC.primary}
              point={{
                size: 4,
                shape: 'circle',
                style: {
                  fill: CHART_SEMANTIC.surface,
                  stroke: CHART_SEMANTIC.primary,
                  lineWidth: 2,
                },
              }}
              lineStyle={{ lineWidth: 2 }}
              legend={false}
              yAxis={{
                grid: {
                  line: {
                    style: { lineDash: [4, 4], stroke: CHART_AXIS.grid },
                  },
                },
              }}
              xAxis={{ label: { autoHide: true } }}
            />
          </ChartCard>
        </SectionCard>
      )}

      {/* ==================== 指导课管理 ==================== */}
      <SectionCard title="指导课管理" subtitle="课时销售与核销">
        <div className={styles.kpiRow}>
          <StatCard
            title="7 日内到期"
            value={(guidance.summary || {}).expireIn7DaysNum}
            unit="节"
            tips="需关注课时"
          />
          <StatCard
            title="剩余课时数"
            value={(guidance.summary || {}).remainCourseNum}
            unit="节"
          />
          <StatCard
            title="累计销售课时"
            value={(guidance.summary || {}).totalSoldNum}
            unit="节"
          />
          <StatCard
            title="累计核销课时"
            value={(guidance.summary || {}).totalUsedNum}
            unit="节"
          />
        </div>
      </SectionCard>

      <div className={styles.chartRow}>
        <SectionCard
          title="销售与核销趋势"
          subtitle="最近24周（最近半年）销售与核销课时对比"
        >
          <ChartCard height={300} empty={!sellData.length}>
            <Area
              height={300}
              data={sellData}
              xField="label"
              yField="value"
              seriesField="type"
              smooth
              color={[CHART_SEMANTIC.primary, CHART_SEMANTIC.secondary]}
              /* h5 同款竖向渐变（顶部 30% → 底部 5%） */
              gradient={{ type: 'linear', x: 0, y: 0, x2: 0, y2: 1 }}
              legend={{ position: 'top-left' }}
              xAxis={{
                label: { autoHide: true, style: { fontSize: 10 } },
              }}
              yAxis={{
                title: {
                  text: '课时数（节）',
                  style: { fontSize: 12, fill: CHART_AXIS.axisLabel },
                },
                grid: {
                  line: {
                    style: { lineDash: [4, 4], stroke: CHART_AXIS.grid },
                  },
                },
              }}
            />
          </ChartCard>
        </SectionCard>

        <SectionCard
          title="课程核销趋势"
          subtitle="最近24周（最近半年）核销课时数对比"
        >
          <ChartCard height={300} empty={!courseData.length}>
            <Line
              height={300}
              smooth
              data={courseData}
              xField="label"
              yField="value"
              seriesField="type"
              /* G2Plot 在 seriesField 下，color 回调收到的是**系列名**而不是数据行；
                 之前写成 (d) => ...d.__idx 永远取不到，5 个系列全落到色板第 0 个蓝。 */
              /* seriesField 存在时 G2Plot 用色阶接管颜色，传回调会被覆盖
                 （实测两版回调都退化成全蓝）。可靠写法是**按系列出现顺序
                 传颜色数组**——courseData 正是按 courseSeries 顺序拼的。 */
              color={courseSeries.map(
                (_, i) => CHART_SERIES[i % CHART_SERIES.length],
              )}
              legend={{ position: 'top-left' }}
              xAxis={{
                label: { autoHide: true, style: { fontSize: 10 } },
              }}
              yAxis={{
                title: {
                  text: '核销课时数（节）',
                  style: { fontSize: 12, fill: CHART_AXIS.axisLabel },
                },
                grid: {
                  line: {
                    style: { lineDash: [4, 4], stroke: CHART_AXIS.grid },
                  },
                },
              }}
            />
          </ChartCard>
        </SectionCard>
      </div>

      <SectionCard title="会员课时明细" subtitle="所有会员的购课与核销情况">
        <div className={styles.searchRow}>
          <Input.Search
            className={styles.searchInput}
            placeholder="按会员昵称/手机号搜索…"
            allowClear
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
              /* 清空时回到全量 */
              if (!e.target.value) {
                setSearch('');
                setFilter('coursePage', 1);
              }
            }}
            onSearch={(v) => {
              setSearch(v || '');
              setFilter('coursePage', 1);
            }}
            enterButton="搜索"
          />
        </div>

        <FilterChips
          options={COURSE_FILTERS}
          value={courseFilter}
          onChange={(v) => {
            setAll({ courseFilter: v, coursePage: 1 });
          }}
        />

        <DataTable
          size="small"
          rowKey="userId"
          columns={courseDetailColumns}
          dataSource={courseRows}
          emptyText="暂无会员课时数据"
          emptyHint="可切换筛选条件或搜索其它会员"
          pagination={{
            current: guidance.currentPage || coursePage,
            pageSize: COURSE_PAGE_SIZE,
            total: guidance.total || 0,
            showSizeChanger: false,
            onChange: (page) => setFilter('coursePage', page),
          }}
        />
      </SectionCard>
    </div>
  );
};

export default ServiceDelivery;
